import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import EyeIcon from '../../components/icons/EyeIcon';
import CancelContractIcon from '../../components/icons/CancelContractIcon'; // Reusing for "cancel request"
import SearchIcon from '../../components/icons/SearchIcon';
import DynamicTable from '../../components/Table/DynamicTable';
import Pagination from '../../components/Pagination/Pagination';

import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const RequestListAgentScreen = () => {
    const navigate = useNavigate();
    const { token } = useAuth();

    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Options for the status filter based on HTML
    const statusOptions = [
        { value: "", label: "Filtrar por estado" },
        { value: "pending", label: "Pendiente" },
        { value: "approved", label: "Aprobada" },
        { value: "rejected", label: "Rechazada" },
    ];

    // Status styles mapping for badges
    const statusStyles = {
        pending: { label: "Pendiente", className: "bg-warning text-dark" },
        approved: { label: "Aprobada", className: "bg-success" },
        rejected: { label: "Rechazada", className: "bg-danger" },
    };

    // Helper to extract travel date range from form/answers (step1_4*)
    const extractTravelDateRange = (formObj) => {
        if (!formObj || typeof formObj !== 'object') return 'N/A';
        // Find the first key that starts with 'step1_4'
        const key = Object.keys(formObj).find(k => k.startsWith('step1_4'));
        if (key && formObj[key]) {
            try {
                const val = typeof formObj[key] === 'string' ? JSON.parse(formObj[key]) : formObj[key];
                if (val && val.startDate && val.endDate) {
                    return `${val.startDate} a ${val.endDate}`;
                }
            } catch (e) {
                // ignore parse error
            }
        }
        return 'N/A';
    };

    // Fetch requests from users and user-trips tables
    useEffect(() => {
        const fetchRequests = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // 1. Fetch all users
                const usersRes = await fetch(`${API_BASE_URL}/api/users`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!usersRes.ok) throw new Error('No se pudieron obtener los usuarios.');
                const users = await usersRes.json();

                // 2. Fetch all user-trips
                const tripsRes = await fetch(`${API_BASE_URL}/api/user-trips`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!tripsRes.ok) throw new Error('No se pudieron obtener los viajes de usuario.');
                const userTrips = await tripsRes.json();

                // 3. Map userId to user for quick lookup
                const userMap = new Map(users.map(u => [u.id, u]));

                // 4. Helper to get status
                const getStatus = (trip) => {
                    switch (trip.status) {
                        case 'pending':
                        case 'Pendiente':
                            return 'pending';
                        case 'approved':
                        case 'Aprobada':
                            return 'approved';
                        case 'rejected':
                        case 'Rechazada':
                            return 'rejected';
                        default:
                            return 'pending';
                    }
                };

                // 5. Build synthetic "first trip" from initial_form_json for each user
                const syntheticTrips = users
                    .filter(user => user.initial_form_json)
                    .map(user => {
                        let initialForm = {};
                        if (user.initial_form_json) {
                            if (typeof user.initial_form_json === 'string') {
                                try {
                                    initialForm = JSON.parse(user.initial_form_json);
                                } catch (e) {
                                    initialForm = {};
                                }
                            } else if (typeof user.initial_form_json === 'object') {
                                initialForm = user.initial_form_json;
                            } else {
                                initialForm = {};
                            }
                        }
                        // Extract travel dates from step1_4* field
                        const travelDateRange = extractTravelDateRange(initialForm);
                        // Use user.created_at if available, else N/A
                        const requestDate = user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A';

                        return {
                            id: `user-${user.id}-initial`,
                            applicantName: user.name || user.username || user.email || 'N/A',
                            requestDate,
                            scheduleDate: 'N/A',
                            travelDateRange,
                            status: 'pending', // Assume initial form is always pending, or adjust as needed
                            initialForm,
                            answer: {}, // No answer for initial form, keep field name consistent
                            isInitialForm: true,
                        };
                    });

                // 6. Build normal trips from user-trips
                const normalTrips = userTrips.map(trip => {
                    const user = userMap.get(trip.user_id) || {};
                    let initialForm = {};
                    if (user.initial_form_json) {
                        if (typeof user.initial_form_json === 'string') {
                            try {
                                initialForm = JSON.parse(user.initial_form_json);
                            } catch (e) {
                                initialForm = {};
                            }
                        } else if (typeof user.initial_form_json === 'object') {
                            initialForm = user.initial_form_json;
                        } else {
                            initialForm = {};
                        }
                    }
                    let answer = {};
                    try {
                        answer = trip.answer ? JSON.parse(trip.answer) : {};
                    } catch (e) {
                        answer = {};
                    }
                    // Extract travel dates from step1_4* field in answer
                    const travelDateRange = extractTravelDateRange(answer);
                    return {
                        id: trip.id || `REQ-${trip.user_id}-${trip.id}`,
                        applicantName: user.name || user.username || user.email || 'N/A',
                        requestDate: trip.created_at ? new Date(trip.created_at).toLocaleDateString() : 'N/A',
                        scheduleDate: trip.schedule_date ? new Date(trip.schedule_date).toLocaleDateString() : 'N/A',
                        travelDateRange,
                        status: getStatus(trip),
                        initialForm,
                        answer,
                        isInitialForm: false,
                    };
                });

                // 7. Combine synthetic and normal trips
                const requestsData = [...syntheticTrips, ...normalTrips];

                setRequests(requestsData);
            } catch (err) {
                setError(err.message);
                setRequests([]);
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            fetchRequests();
        }
    }, [token]);

    // Define columns for the DynamicTable
    const requestColumns = [
        { header: 'Nombre solicitante', accessor: 'applicantName' },
        { header: 'Fecha de Solicitud', accessor: 'requestDate' },
        //{ header: 'Fecha Agendamiento', accessor: 'scheduleDate' },
        { header: 'Fecha de Viaje (Rango)', accessor: 'travelDateRange' },
        {
            header: 'Estado',
            render: (request) => {
                const statusInfo = statusStyles[request.status] || { label: request.status, className: 'bg-secondary' };
                return (
                    <span className={`badge rounded-pill ${statusInfo.className}`}>
                        {statusInfo.label}
                    </span>
                );
            }
        },
        {
            header: 'Acciones',
            render: (request) => (
                <>
                    {request.status === 'pending' ? (
                        <Link to={`/requests/${request.id}`} className="btn btn-action-round-outline btn-outline-info-checkin me-1" data-bs-toggle="tooltip" title="Ver Itinerario">
                            <EyeIcon />
                        </Link>
                    ) : (
                        <button className="btn btn-action-round-outline btn-outline-info-checkin" onClick={() => console.log('View request:', request.id)} data-bs-toggle="tooltip" title="Ver Solicitud">
                            <EyeIcon />
                        </button>
                    )}
                    {request.status === 'pending' && (
                        <button className="btn btn-action-round-outline btn-outline-danger-checkin" onClick={() => console.log('Cancel request:', request.id)} data-bs-toggle="tooltip" title="Cancelar Solicitud">
                            <CancelContractIcon />
                        </button>
                    )}
                </>
            )
        }
    ];

    // Define which columns are searchable
    const searchableColumns = ['applicantName', 'requestDate', 'travelDateRange'];

    // Apply search and filter logic
    const filteredRequests = requests.filter(request => {
        const matchesSearchTerm = searchTerm.trim() === '' ||
            searchableColumns.some(column => {
                const value = String(request[column]).toLowerCase();
                return value.includes(searchTerm.toLowerCase());
            });

        const matchesStatus = filterStatus === '' || request.status === filterStatus;

        return matchesSearchTerm && matchesStatus;
    });

    const totalItems = filteredRequests.length;

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus]);

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-3">
                <div className="pt-3 pb-2">
                    <h1 className="h2 mb-3">Solicitudes</h1>
                    <p className="mb-0 text-muted text-start">Listado y seguimiento de las solicitudes de viaje.</p>
                </div>
            </div>

            <div className="row g-3 mb-3 align-items-center d-flex justify-content-between">
                <div className="col-md-8">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar solicitudes..."
                            aria-label="Buscar solicitudes"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="btn btn-outline-secondary" type="button">
                            <SearchIcon />
                        </button>
                    </div>
                </div>
                <div className="col-md-4">
                    <select
                        className="form-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <div className="card-body px-md-2 px-0">
                            <FormSpinnerOverlay isLoading={isLoading} />
                            {error && <AlertMessage type="danger" message={`Error al cargar solicitudes: ${error}`} />}
                            {!isLoading && !error && filteredRequests.length === 0 && (
                                <AlertMessage type="info" message="No hay solicitudes registradas que coincidan con los criterios." />
                            )}
                            {!isLoading && !error && filteredRequests.length > 0 && (
                                <DynamicTable
                                    data={paginatedRequests}
                                    columns={requestColumns}
                                    keyAccessor="id"
                                />
                            )}
                            {totalPages > 1 && (
                                <Pagination
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={totalItems}
                                    onPageChange={(page) => setCurrentPage(page)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RequestListAgentScreen;